import React from 'react';
import api from 'controllers/raymanAPI';
import {FilesCtxProvider, FilesCtx} from './files';
import Singletons from 'controllers/Singletons';

function map(item)
{
    return {...item, id: item.stepID, items: item.items.map((intItem) => intItem.itemID)}
}

async function getData(procedureID)
{
    let indexed = {};

    let response = await api.procedures.steps.getAll(procedureID);
    let data = await response.json();
    data = data.map((item)=>map(item));
    data.forEach(item => {
        indexed[item.stepID] = item
    });
    
    return {array: data, indexed: indexed};
}

const ProcedureStepsCtx = React.createContext({
    loading: true, 
    data: {
        array: [], 
        indexed: {}
    }, 
    funcs: {
        create: () => {},
        update: () => {},
        delete: () => {},
    }
});

function ProcedureStepsCtxProvider({procedureID, ...props})
{

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState({array: [], indexed: []});

    React.useEffect(() => {
        if(!procedureID) return;
        getData(procedureID).then(values => {
            setData(values);
            setLoading(false);
        });
    }, [procedureID]);

    const internalRefresh = React.useCallback(() => {
        getData(procedureID).then(values => {
            setData(values);
            setLoading(false);
        });
    }, [procedureID]);

    const internalUpdate = React.useCallback((stepID, stepData) => {
        let tmp = {...data};
        if(stepData.order && stepData.order !== tmp.indexed[stepID].order) internalRefresh();
        else {
            Object.keys(stepData).forEach((key) => {
                tmp.indexed[stepID][key] = stepData[key];
            })
            setData(tmp);
        }
    }, [data, internalRefresh]);

    const internalAdd = React.useCallback(async (stepID, item) => {
        if(!procedureID) return;
        if(data.indexed[stepID] !== undefined) return internalUpdate(stepID, map(item));
        
        let tmp = {...data};
        item = map(item);
        let index = tmp.array.push(item);
        tmp.indexed[item.id] = tmp.array[index-1];
        setData(tmp);
    }, [internalUpdate, procedureID, data]);

    
    const internalDelete = React.useCallback((stepID) => {
        let tmp = {...data};
        let item = tmp.indexed[stepID];

        // remove from array
        let index = tmp.array.indexOf(item);
        if(index > -1) tmp.array.splice(index,1);

        delete tmp.indexed[stepID];
        setData(tmp);
    }, [data]);    


    const create = React.useCallback(async (data) => {
        if(!procedureID) return;
        let result = await api.procedures.steps.post(procedureID, data);
        if(result.ok)
        {
            let step = await result.json();
            await internalAdd(step.stepID, step);
            return step;
        }
    }, [procedureID, internalAdd]);

    const update = React.useCallback(async (stepID, data) => {
        if(!procedureID) return;
        let result = await api.procedures.steps.updateSingle(procedureID, stepID, data);
        if(result.ok) internalUpdate(stepID, map(await result.json()));

    }, [procedureID, internalUpdate]);

    const remove = React.useCallback(async (stepID) => {
        if(!procedureID) return;
        let result = await api.procedures.steps.deleteSingle(procedureID, stepID);
        if(result.ok) internalDelete(stepID);
    }, [procedureID, internalDelete]);

    React.useEffect( function handleNotifications() {

        const handleCreate = (message) => { if(message.procedureID === procedureID) internalAdd(message.stepID, message); }
        const handleUpdate = (message) => { if(message.procedureID === procedureID) internalUpdate(message.stepID, map(message));}
        const handleDelete = (message) => { if(message.procedureID === procedureID) internalDelete(message.stepID, message);}

        Singletons.NotificationsSocket.on('procedures.steps.created', handleCreate);
        Singletons.NotificationsSocket.on('procedures.steps.updated', handleUpdate);
        Singletons.NotificationsSocket.on('procedures.steps.deleted', handleDelete);

        return () => {
            Singletons.NotificationsSocket.off('procedures.steps.created', handleCreate);
            Singletons.NotificationsSocket.off('procedures.steps.updated', handleUpdate);
            Singletons.NotificationsSocket.off('procedures.steps.deleted', handleDelete);
        }

    }, [internalAdd, internalUpdate, internalDelete, procedureID]);

    return <ProcedureStepsCtx.Provider value={{loading: loading, data: {...data}, funcs: {create: create, update: update, delete: remove}}} >
        {props.children}
        </ProcedureStepsCtx.Provider>
}


function ProcedureStepFilesProvider({procedureID, stepID, ...props})
{
    // APIParams is built as a react state to avoid triggering changes that fetch the data again on FilesCtxProvider
    // if we build an array on the fly, every rerender the array is different (even with the same values) and triggers a new fetch
    const [params, setParams] = React.useState([procedureID, stepID]);

    React.useEffect(function updateApiParams()
    {
        setParams([procedureID, stepID]);
    }, [procedureID, stepID]);

    return <FilesCtxProvider apiEndpoint={api.procedures.steps.files} apiParams={params}>
            <ProcedureStepFilesNotificationHandler procedureID={procedureID} stepID={stepID}>
                {props.children}
            </ProcedureStepFilesNotificationHandler>
        </FilesCtxProvider>
}


function ProcedureStepFilesNotificationHandler({procedureID, stepID, ...props})
{
    const filesCtx = React.useContext(FilesCtx);

    React.useEffect( function handleChatNotifications() {

        const addFile = (message) => {if(message.procedureID === procedureID && message.stepID === stepID) filesCtx.funcs.internal.add(message.fileInfo.fileID, message.fileInfo);}
        const deleteFile = (message) => {if(message.procedureID === procedureID && message.stepID === stepID) filesCtx.funcs.internal.delete(message.fileID);}
        const refreshFile = (message) => {if(message.procedureID === procedureID && message.stepID === stepID) filesCtx.funcs.internal.refresh(message.fileInfo.fileID);}

        Singletons.NotificationsSocket.on('procedures.steps.files.created', addFile);
        Singletons.NotificationsSocket.on('procedures.steps.files.deleted', deleteFile);
        Singletons.NotificationsSocket.on('procedures.steps.files.updated', refreshFile);

        return () => {
            Singletons.NotificationsSocket.off('procedures.steps.files.created', addFile);
            Singletons.NotificationsSocket.off('procedures.steps.files.deleted', deleteFile);
            Singletons.NotificationsSocket.off('procedures.steps.files.updated', refreshFile);
        }
    }, [filesCtx, procedureID, stepID]);

    return <>{props.children}</>;

}

export {ProcedureStepsCtx, ProcedureStepsCtxProvider, ProcedureStepFilesProvider};
export {FilesCtx as ProcedureStepFilesCtx} from './files';